import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Users,
  Mail,
  Shield,
  Edit,
  Eye,
  UserX,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2,
  Settings,
  Loader2,
} from "lucide-react";
import {
  GuestInvitationService,
  GuestInvitation,
  GuestPermission,
} from "@/services/guestInvitationService";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { useUserSubscription } from "@/utils/subscriptionUtils";
import InviteGuestModal from "./InviteGuestModalNew";
import styles from "./GuestManagementPanel.module.css";

interface GuestManagementPanelProps {
  projectId?: string | null;
}

export function GuestManagementPanel({ projectId }: GuestManagementPanelProps) {
  const [invitations, setInvitations] = useState<GuestInvitation[]>([]);
  const [guestPermissions, setGuestPermissions] = useState<GuestPermission[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const { toast } = useToast();
  const { user } = useSupabaseAuth();
  const { tier } = useUserSubscription();

  const planDisplay =
    tier === "PAYG"
      ? "(1 credit/invite)"
      : `${tier.charAt(0).toUpperCase() + tier.slice(1)} Plan`;

  const loadData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const [invitationsData, permissionsData] = await Promise.all([
        GuestInvitationService.getHostInvitations(user.id),
        GuestInvitationService.getHostGuestPermissions(user.id),
      ]);

      setInvitations(invitationsData);
      setGuestPermissions(permissionsData);
    } catch (error) {
      console.error("Error loading guest data:", error);
      toast({
        title: "Error",
        description: "Failed to load guest information",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user]);

  // Filter invitations by status for better UX (exclude deleted ones)
  const pendingInvitations = invitations.filter(
    (inv) => inv.status === "pending",
  );
  const completedInvitations = invitations.filter((inv) =>
    ["accepted", "declined", "cancelled", "expired"].includes(inv.status),
  );

  const handleUpdatePermissions = async (
    guestUserId: string,
    projectId: string | null | undefined,
    newPermissions: { view: boolean; edit: boolean },
  ) => {
    if (!user) return;

    setActionLoading(`permission-${guestUserId}`);
    try {
      const result = await GuestInvitationService.updateGuestPermissions(
        user.id,
        guestUserId,
        projectId || null,
        newPermissions,
      );

      if (result.success) {
        toast({
          title: "Permissions Updated",
          description: "Guest permissions have been updated successfully",
          variant: "default",
        });
        loadData();
      } else {
        toast({
          title: "Update Failed",
          description: result.error || "Failed to update permissions",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveGuest = async (guestUserId: string, guestEmail: string) => {
    if (!user) return;

    setActionLoading(`remove-${guestUserId}`);
    try {
      const result = await GuestInvitationService.removeGuest(
        user.id,
        guestUserId,
      );

      if (result.success) {
        toast({
          title: "Guest Removed",
          description: `${guestEmail} has been removed from the project`,
          variant: "default",
        });
        loadData();
      } else {
        toast({
          title: "Removal Failed",
          description: result.error || "Failed to remove guest",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearHistory = async () => {
    if (!user) return;

    setActionLoading("clear-history");
    try {
      const result = await GuestInvitationService.clearInvitationHistory(
        user.id,
      );

      if (result.success) {
        toast({
          title: "History Cleared",
          description: "Invitation history has been cleared successfully",
          variant: "default",
        });
        loadData();
      } else {
        toast({
          title: "Clear Failed",
          description: result.error || "Failed to clear history",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4 text-amber-500" />;
      case "accepted":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "declined":
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "cancelled":
        return <UserX className="h-4 w-4 text-gray-500" />;
      case "deleted":
        return <Trash2 className="h-4 w-4 text-gray-400" />;
      case "expired":
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200";
      case "accepted":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "declined":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      case "cancelled":
        return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
      case "deleted":
        return "bg-gray-50 text-gray-500 dark:bg-gray-900 dark:text-gray-500";
      case "expired":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Guest Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#40e0d0]"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {/* Modern Header Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#40e0d0]/5 via-white/50 to-emerald-50/30 dark:from-[#40e0d0]/10 dark:via-gray-800/50 dark:to-gray-900/30 border border-[#40e0d0]/20 backdrop-blur-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-[#40e0d0]/5 to-transparent" />
        <div className="relative px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#40e0d0] to-emerald-500 flex items-center justify-center shadow-lg">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div className="text-left">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Team Collaboration
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Manage project access and permissions
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-[#40e0d0]/20">
                <div className="w-2 h-2 rounded-full bg-[#40e0d0] animate-pulse" />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                  {planDisplay}
                </span>
              </div>
              <InviteGuestModal onInvitationSent={loadData} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6">
        {/* Active Team Members - Modern Card Design */}
        {guestPermissions.length > 0 && (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <Shield className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Active Team Members
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-left">
                      {guestPermissions.length} member
                      {guestPermissions.length !== 1 ? "s" : ""} with access
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                    {guestPermissions.length} Active
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-3">
              {guestPermissions.map((permission, index) => (
                <div
                  key={permission.id}
                  className={`group relative flex items-center justify-between p-4 rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-gradient-to-r from-white to-gray-50/50 dark:from-gray-800 dark:to-gray-800/50 hover:shadow-lg hover:scale-[1.01] transition-all duration-300 hover:border-[#40e0d0]/30 animation-delay-${index}`}
                >
                  {/* Decorative line */}
                  <div className="absolute left-0 top-1/2 w-1 h-8 bg-gradient-to-b from-[#40e0d0] to-emerald-500 rounded-r-full transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div className="flex items-center gap-4 flex-1">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#40e0d0]/20 to-emerald-500/20 flex items-center justify-center border-2 border-white dark:border-gray-700 shadow-sm">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#40e0d0] to-emerald-500 flex items-center justify-center">
                          <Mail className="h-3 w-3 text-white" />
                        </div>
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white dark:border-gray-800 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {permission.guest_user?.email ||
                            `Guest User (ID: ${permission.guest_user_id.slice(0, 8)}...)`}
                        </p>
                        {!permission.guest_user?.email && (
                          <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded">
                            User data unavailable
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/30">
                          <Eye className="h-3 w-3 text-green-600 dark:text-green-400" />
                          <span className="text-xs font-medium text-green-700 dark:text-green-400">
                            View
                          </span>
                        </div>
                        {permission.permissions.edit && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30">
                            <Edit className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                            <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                              Edit
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Edit Access
                      </span>
                      <Switch
                        checked={permission.permissions.edit}
                        onCheckedChange={(checked) =>
                          handleUpdatePermissions(
                            permission.guest_user_id,
                            permission.project_id,
                            {
                              view: true,
                              edit: checked,
                            },
                          )
                        }
                        disabled={
                          actionLoading ===
                          `permission-${permission.guest_user_id}`
                        }
                        className="data-[state=checked]:bg-[#40e0d0]"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        handleRemoveGuest(
                          permission.guest_user_id,
                          permission.guest_user?.email ||
                            `Guest User (${permission.guest_user_id.slice(0, 8)}...)`,
                        )
                      }
                      disabled={
                        actionLoading === `remove-${permission.guest_user_id}`
                      }
                      className="h-8 w-8 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      {actionLoading ===
                      `remove-${permission.guest_user_id}` ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <UserX className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Invitations - Modern Design */}
        {pendingInvitations.length > 0 && (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl border border-amber-200/50 dark:border-amber-800/50 shadow-sm">
            <div className="px-6 py-4 border-b border-amber-200/50 dark:border-amber-800/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
                    <Mail className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Pending Invitations
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {pendingInvitations.length} invitation
                      {pendingInvitations.length !== 1 ? "s" : ""} awaiting
                      response
                    </p>
                  </div>
                </div>
                <div className="px-2 py-1 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium">
                  {pendingInvitations.length} Pending
                </div>
              </div>
            </div>
            <div className="p-6 space-y-3">
              {pendingInvitations.map((invitation, index) => (
                <div
                  key={invitation.id}
                  className="group relative flex items-center justify-between p-4 rounded-xl border border-gray-200/60 dark:border-gray-700/60 bg-gradient-to-r from-white to-amber-50/30 dark:from-gray-800 dark:to-amber-900/5 hover:shadow-md transition-all duration-300"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-100/80 to-orange-100/80 dark:from-amber-900/30 dark:to-orange-900/30 flex items-center justify-center border-2 border-white dark:border-gray-700 shadow-sm">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                          {getStatusIcon(invitation.status)}
                        </div>
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-amber-500 border-2 border-white dark:border-gray-800 flex items-center justify-center">
                        <Clock className="h-2 w-2 text-white" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {invitation.guest_email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>
                          Invited{" "}
                          {new Date(invitation.invited_at).toLocaleDateString()}
                        </span>
                        <span>•</span>
                        <span>
                          Expires{" "}
                          {new Date(invitation.expires_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div
                      className={`px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(invitation.status)}`}
                    >
                      {invitation.status.charAt(0).toUpperCase() +
                        invitation.status.slice(1)}
                    </div>
                    {invitation.permissions.edit && (
                      <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-100 dark:bg-blue-900/30">
                        <Edit className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                        <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                          Edit Access
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Invitation History - Collapsed by default */}
        {completedInvitations.length > 0 && (
          <details className="group">
            <summary className="cursor-pointer bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50 p-4 hover:bg-white/70 dark:hover:bg-gray-800/70 transition-all duration-200 list-none [&::-webkit-details-marker]:hidden">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <AlertCircle className="h-3 w-3 text-gray-500" />
                  </div>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">
                      Invitation History
                    </span>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      {completedInvitations.length} completed
                    </span>
                  </div>
                </div>
                <div className="text-xs text-gray-400 group-open:rotate-90 transition-transform duration-200">
                  ▶
                </div>
              </div>
            </summary>
            <div className="mt-3 bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl border border-gray-200/50 dark:border-gray-700/50 p-4 space-y-2">
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200/50 dark:border-gray-700/50">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {completedInvitations.length} completed invitation
                  {completedInvitations.length !== 1 ? "s" : ""}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearHistory}
                  disabled={actionLoading === "clear-history"}
                  className="h-7 px-2 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  {actionLoading === "clear-history" ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Clearing...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-3 w-3 mr-1" />
                      Clear History
                    </>
                  )}
                </Button>
              </div>
              {completedInvitations.map((invitation, index) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50/50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                      {getStatusIcon(invitation.status)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">
                        {invitation.guest_email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {invitation.status === "accepted" &&
                        invitation.accepted_at
                          ? `Accepted ${new Date(invitation.accepted_at).toLocaleDateString()}`
                          : `${invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)} ${new Date(invitation.invited_at).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  <div
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invitation.status)}`}
                  >
                    {invitation.status.charAt(0).toUpperCase() +
                      invitation.status.slice(1)}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Modern Empty State */}
        {pendingInvitations.length === 0 &&
          guestPermissions.length === 0 &&
          completedInvitations.length === 0 && (
            <div className="relative overflow-hidden bg-gradient-to-br from-gray-50/80 via-white to-[#40e0d0]/5 dark:from-gray-800/80 dark:via-gray-900 dark:to-[#40e0d0]/10 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-sm">
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/20 to-[#40e0d0]/10 dark:from-transparent dark:via-gray-800/20 dark:to-[#40e0d0]/5" />
              <div className="relative px-8 py-12 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#40e0d0]/20 to-emerald-500/20 mb-6 shadow-lg">
                  <Users className="h-10 w-10 text-[#40e0d0]" />
                </div>
                <div className="max-w-sm mx-auto space-y-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Start Collaborating
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      Invite your team members to collaborate on environmental
                      projects. Share insights, track progress, and work
                      together towards sustainability goals.
                    </p>
                  </div>
                  <div className="pt-4">
                    <InviteGuestModal onInvitationSent={loadData} />
                  </div>
                  <div className="flex items-center justify-center gap-4 pt-4 text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <Shield className="h-3 w-3" />
                      <span>Secure sharing</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      <span>Role-based access</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      <span>Email invitations</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Invite credits information (replaced plan limits) */}
        <div className="relative overflow-hidden bg-gradient-to-br from-indigo-50/80 via-white to-purple-50/30 dark:from-indigo-900/20 dark:via-gray-800 dark:to-purple-900/10 rounded-2xl border border-indigo-200/50 dark:border-indigo-800/30 shadow-sm">
          <div className="relative px-6 py-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
                  <Shield className="h-4 w-4 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-gray-900 dark:text-white ">
                    Invite Credits
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Invite cost and total invites
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <strong>1 guest</strong> = 1 credit
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  <strong>Total number of invites</strong> = 10
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GuestManagementPanel;
